import {
  ArrowDownIcon,
  ArrowUpIcon,
  Bin,
  Checkbox,
  EmptyStateButton,
  Info,
  Input,
  Tooltip,
  Typography,
} from '@weareopenr/component-library';
import styles from '../CreateOptionGroupModal.module.scss';
import WarningBanner from '../../WarningBanner/WarningBanner';
import { GLOBAL_ITEM_UPDATE_NOTE } from '../../../constants';
import { ProductRangeSelector } from '../../ProductRangeSelector/ProductRangeSelector';
import OptionGroupOption from '../OptionGroupOption/OptionGroupOption';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { useRanges } from '../../../hooks/useRanges/useRanges';
import { IOptionGroupValues } from '../types';
import { useEffect, useMemo, useState } from 'react';
import type { IMenuItem, IOptionGroup, TMenuItemType } from 'src/types/menu.types';
import { getAllSelectableItems } from '../utilities/getAllSelectableItems/getAllSelectableItems';
import { useItems } from '../../../hooks/useItems';
import { useInView } from 'react-intersection-observer';
import { ItemType } from '@weareopenr/types';
import { pluralize } from '../../../helpers/pluralize/pluralize';
import type { IOption } from '@weareopenr/component-library';

const newGroupOption = {
  optionItemId: '',
  parentItemId: '',
  name: '',
  posId: '',
  valueWithTax: 0,
  useDefaultPrice: false,
  inheritPosId: true,
};

interface IOptionGroupDetailsProps {
  optionGroup?: IOptionGroup;
  useFormReturn: UseFormReturn<IOptionGroupValues>;
  itemType: TMenuItemType;
}

export const OptionGroupDetails = ({ optionGroup, useFormReturn, itemType }: IOptionGroupDetailsProps) => {
  const isEdit = !!optionGroup;
  const { data: ranges } = useRanges({
    enabled: !isEdit,
  });
  // if a user is editing an option group, they are locked to the ranges already selected
  const availableRanges = (isEdit ? optionGroup?.ranges : ranges) || [];

  const {
    register,
    formState: { errors },
    trigger,
    setValue,
    clearErrors,
    watch,
    control,
  } = useFormReturn;
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'modifiers',
    rules: {
      minLength: 1,
      required: true,
    },
  });

  const isMissingModifiers = errors.modifiers?.root?.type === 'required';
  const isModifiersNotMatchingRange = errors.modifiers?.some((error) => error?.type === 'nonMatchingModifierRange');

  const watchIsUnique = watch('uniqueOnly');
  const watchRanges = watch('ranges');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectableItems, setSelectableItems] = useState<IMenuItem[]>([]);

  const { data, hasNextPage, fetchNextPage } = useItems(
    searchTerm,
    undefined,
    ['STANDARD', 'MODIFIER', 'NON-FOOD'],
    undefined,
    watchRanges,
  );

  const { ref: dataScrollRef } = useInView({
    onChange: (inView) => {
      if (inView && hasNextPage) {
        fetchNextPage();
      }
    },
  });

  const addNewGroupOption = (): void => {
    append(newGroupOption);
    trigger('min_choices');
  };

  const removeGroupOption = (index: number) => {
    remove(index);
    trigger('min_choices');
  };

  useEffect(() => {
    // Always replace selectableItems with the latest data for the current range/search
    const newItems: IMenuItem[] = data?.pages?.flat() || [];
    setSelectableItems(newItems);
  }, [data, watchRanges]);

  const formattedOptionGroupOptions = useMemo(() => {
    return getAllSelectableItems(selectableItems, optionGroup);
  }, [selectableItems, optionGroup]);

  const groupContentNameSingular = itemType === ItemType.BUNDLE ? 'Product' : 'Modifier';
  const groupContentNamePlural = pluralize(groupContentNameSingular, 2);
  const categoryType = itemType === ItemType.BUNDLE ? 'Bundle' : 'Modifier';

  useEffect(() => {
    trigger('min_choices');
  }, [watchIsUnique]);

  return (
    <>
      <Typography size="xxlarge" weight="semi-bold" className={styles['heading-text']}>
        {`${isEdit ? 'Edit' : 'Create'} ${itemType === ItemType.BUNDLE ? 'Bundle' : 'Modifier'} Category`}
      </Typography>
      <Typography size="medium" weight="regular" className={styles['sub-heading-text']}>
        {isEdit ? `Update ${categoryType} category details` : `Add ${categoryType} category details`}
      </Typography>
      <WarningBanner text={GLOBAL_ITEM_UPDATE_NOTE} />
      <section className={styles['section']}>
        <Typography size="xlarge" weight="semi-bold" className={styles['heading-text']}>
          {`${categoryType} category`}
        </Typography>
        <div className={styles['input-wrapper']}>
          <Input
            type="text"
            label="Name"
            hasMargin
            register={register('name', { required: 'Please enter a name' })}
            error={errors?.name?.message}
          />
        </div>
        <div className={styles['input-wrapper']}>
          <Input type="text" label="Consumer Name" hasMargin register={register('consumer_name')} />
        </div>
        {itemType !== 'BUNDLE' && (
          <div className={styles['input-wrapper']}>
            <Input
              type="text"
              label="Description"
              hasMargin
              register={register('description', { required: 'Please enter a description' })}
              rows={2}
              error={errors?.description?.message}
            />
          </div>
        )}
        <div className={styles['tooltip-container']}>
          <Typography>Range of choices</Typography>
          <Tooltip text="e.g. Min 1 & Max 3 means at least one choice is required, plus ability to choose up to 3">
            <Info className={styles['info-icon']} />
          </Tooltip>
        </div>
        <div className={styles['range-container']}>
          <Input
            label="Min"
            hasMargin
            type="number"
            register={register('min_choices', {
              required: true,
              valueAsNumber: true,
              validate: {
                isInRange: (v) => (v >= 0 && v <= 99) || 'Min should be between 0 and 99',
                isUnderMaxValue: (v, formValues) => v <= formValues.max_choices || "Min can't be greater than max",
                isValidWhenNonRepeatable: (v, formValues) => {
                  if (formValues.uniqueOnly && v > formValues.modifiers.length) {
                    return 'Min must be less than total number of available modifiers';
                  }
                  return true;
                },
              },
              onChange: () => {
                trigger(['min_choices', 'max_choices']);
              },
            })}
            error={errors?.min_choices?.message}
          />
          <div className={styles['separator-container']}>
            <span className={styles['separator']} />
          </div>
          <Input
            label="Max"
            type="number"
            register={register('max_choices', {
              required: true,
              valueAsNumber: true,
              validate: {
                isInRange: (v) => (v >= 1 && v <= 99) || 'Max should be between 1 and 99',
                isAboveMin: (v, formValues) => v >= formValues.min_choices || "Max can't be less than min",
              },
              onChange: () => {
                trigger(['min_choices', 'max_choices']);
              },
            })}
            error={errors?.max_choices?.message}
          />
        </div>
        {itemType === 'STANDARD' && (
          <div className={styles['non-repeatable-container']}>
            <div className={styles['tooltip-container']}>
              <Typography>Non-repeatable</Typography>
              <Tooltip text="Selecting 'Non-repeatable' limits customers to choosing one option per modifier group when ordering. If unselected, customers can choose multiple of the same modifier within a group">
                <Info className={styles['info-icon']} />
              </Tooltip>
            </div>
            <Checkbox name="uniqueOnly" register={register} label="Unique modifiers only" labelWeight="regular" />
          </div>
        )}
        {itemType === ItemType.STANDARD && (
          <ProductRangeSelector
            ranges={availableRanges}
            register={register}
            setValue={setValue}
            errors={errors}
            watch={watch}
            clearErrors={clearErrors}
            trigger={trigger}
            productType={ItemType.STANDARD}
            control={control}
            isDisabled={isEdit}
          />
        )}
      </section>

      <section className={styles['section']}>
        <Typography size="xlarge" weight="semi-bold" className={styles['heading-text']}>
          {groupContentNamePlural}
        </Typography>
        {isModifiersNotMatchingRange && (
          <div className={styles['error-message']}>
            Modifier does not belong to the range selected <Info className={styles['info-icon']} />
          </div>
        )}
        {fields.length > 0 && (
          <div className={styles['field-group-headings']} data-testid="field-group-headings">
            <Typography weight="semi-bold">Product Name</Typography>
            <Typography weight="semi-bold" className={styles['centered-heading']}>
              Inherit POS ID
            </Typography>
            <Typography weight="semi-bold">Override POS ID</Typography>
          </div>
        )}
        {fields?.map((field, index) => {
          const options: IOption[] = [
            {
              id: 0,
              name: 'Move up',
              isDisabled: index === 0,
              onOptionClick: () => move(index, index - 1),
              persistMenuOnClick: true,
              icon: ArrowUpIcon,
            },
            {
              id: 1,
              name: 'Move down',
              isDisabled: index + 1 === fields.length,
              onOptionClick: () => move(index, index + 1),
              persistMenuOnClick: true,
              icon: ArrowDownIcon,
            },
            { id: 'delete-divider', isDivider: true },
            {
              id: 2,
              name: 'Delete',
              onOptionClick: () => removeGroupOption(index),
              icon: Bin,
              hoverColour: 'tomato',
            },
          ];

          return (
            <OptionGroupOption
              key={field.id}
              index={index}
              modifiers={formattedOptionGroupOptions}
              ellipsisMenuOptions={options}
              useFormReturn={useFormReturn}
              dataScrollRef={dataScrollRef}
              handleDeleteModifier={() => removeGroupOption(index)}
              setSearchTerm={setSearchTerm}
              groupContentNameSingular={groupContentNameSingular}
            />
          );
        })}
        {isMissingModifiers && (
          <div className={styles['error-message']}>
            {groupContentNameSingular} needs to be added <Info className={styles['info-icon']} />
          </div>
        )}
        <div className={styles['add-modifier-container']}>
          <EmptyStateButton
            label={`+ Add ${groupContentNameSingular}`}
            onClick={() => {
              addNewGroupOption();
            }}
          />
        </div>
      </section>
    </>
  );
};
